// Cave84 — Cellars service (Phase 0 Day 4)
// Pure data-access layer over the `cellars` Firestore collection. All hooks
// in apps/web/src/hooks/useCellars.ts should be the only consumers; pages
// must NOT import from this module directly so we can swap the data store
// without touching UI.

import {
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type DocumentReference
} from 'firebase/firestore';
import type { Cellar } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';

const COLLECTION = 'cellars';

// Fields the caller is responsible for at create time. id + denormalized
// counts + created_at are owned by Firestore.
export type CellarCreateInput = Omit<
  Cellar,
  'id' | 'created_at' | 'bottle_count' | 'total_value_eur'
>;

export type CellarUpdateInput = Partial<
  Omit<Cellar, 'id' | 'created_at' | 'bottle_count' | 'total_value_eur'>
>;

function cellarsRef() {
  return typedCollection<Cellar>(COLLECTION);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listCellars(): Promise<Cellar[]> {
  const snap = await getDocs(query(cellarsRef(), orderBy('sort_order', 'asc')));
  return snap.docs.map((d) => d.data());
}

export async function getCellar(id: string): Promise<Cellar | null> {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Cellar, 'id'>) };
}

// ---------------------------------------------------------------------------
// Mutations (owner-only — guarded by Firestore rules and the hook layer).
// ---------------------------------------------------------------------------

export async function createCellar(input: CellarCreateInput): Promise<Cellar> {
  const payload = {
    ...input,
    bottle_count: 0,
    total_value_eur: 0,
    created_at: serverTimestamp()
  };
  // We don't use `addDoc(cellarsRef(), payload)` directly because the
  // converter strips fields with `id`; serverTimestamp() needs the raw
  // collection ref so Firestore SDK can resolve it server-side.
  const rawRef = await addDoc(
    typedCollection<Cellar>(COLLECTION),
    payload as unknown as Cellar
  );
  const fresh = await getDoc(rawRef as DocumentReference<Cellar>);
  const data = fresh.data();
  if (!data) throw new Error(`Cellar ${rawRef.id} disappeared after creation.`);
  return data;
}

export async function updateCellar(id: string, patch: CellarUpdateInput): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, patch);
}

/**
 * F8: cellars are deleted only when empty. The check lives in the rules and
 * in the hook layer (which queries bottles first); this function does the raw
 * delete and trusts the caller.
 */
export async function deleteCellarRaw(id: string): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await deleteDoc(ref);
}
